import api from './api';

/**
 * Analytics API client.
 *
 * Every admin call takes the same filter object; the backend resolves it into
 * one scope so tiles, charts and tables on a screen always agree on the period.
 *
 * filters: { preset, startDate, endDate, granularity, teamId, recognitionTypeId, compare, orgId }
 */
const ADMIN = '/api/v1/admin/analytics';
const USER = '/api/v1/analytics';

export const toParams = (filters = {}) => {
  const params = {};
  if (filters.startDate && filters.endDate) {
    params.start_date = filters.startDate;
    params.end_date = filters.endDate;
  } else if (filters.preset) {
    params.preset = filters.preset;
  }
  if (filters.granularity) params.granularity = filters.granularity;
  if (filters.teamId && filters.teamId !== 'all') params.team_id = filters.teamId;
  if (filters.recognitionTypeId && filters.recognitionTypeId !== 'all') {
    params.recognition_type_id = filters.recognitionTypeId;
  }
  if (filters.compare === false) params.compare = false;
  if (filters.orgId) params.org_id = filters.orgId;
  return params;
};

const get = async (path, filters, extra = {}) => {
  const response = await api.get(path, { params: { ...toParams(filters), ...extra } });
  return response.data;
};

const analyticsService = {
  getFilters: (filters) => get(`${ADMIN}/filters`, filters),
  getOverview: (filters) => get(`${ADMIN}/overview`, filters),
  getInsights: (filters) => get(`${ADMIN}/insights`, filters),
  getTimeseries: (filters) => get(`${ADMIN}/timeseries`, filters),
  getBreakdowns: (filters) => get(`${ADMIN}/breakdowns`, filters),
  getParticipation: (filters) => get(`${ADMIN}/participation`, filters),
  getNetwork: (filters) => get(`${ADMIN}/network`, filters),
  getLeaderboards: (filters, limit = 10) => get(`${ADMIN}/leaderboards`, filters, { limit }),
  getPoints: (filters) => get(`${ADMIN}/points`, filters),
  getNominations: (filters) => get(`${ADMIN}/nominations`, filters),
  getRewards: (filters) => get(`${ADMIN}/rewards`, filters),
  getEngagement: (filters) => get(`${ADMIN}/engagement`, filters),
  getReport: (filters) => get(`${ADMIN}/report`, filters),

  /**
   * Download a CSV dataset for the current filters. Triggers a browser save.
   */
  exportCsv: async (dataset, filters) => {
    const response = await api.get(`${ADMIN}/export/${dataset}`, {
      params: toParams(filters),
      responseType: 'blob',
    });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${dataset}.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return filename;
  },

  // User-facing
  getMyInsights: (preset = '90d') =>
    api.get(`${USER}/me`, { params: { preset } }).then((r) => r.data),
  getAnalysableTeams: () => api.get(`${USER}/teams`).then((r) => r.data),
  getTeamInsights: (teamId, filters) => get(`${USER}/teams/${teamId}`, filters),
};

export default analyticsService;
